import { injectable } from 'tsyringe';
import type { PlanSummary } from '@bmai/shared';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import {
    OperatorTenantsService,
    type EntitlementInput,
    type OperatorEntitlement,
    type OperatorTenant,
    type ProvisionHandoff,
    type ProvisionInput,
} from './OperatorTenantsService';
import { OperatorPlansService, type QuotaBucket } from './plans/OperatorPlansService';
import { OperatorHealthService, type AccountHealthRow } from './OperatorHealthService';

@injectable()
export class OperatorTenantsViewModel extends BaseViewModel {
    tenants: OperatorTenant[] | null = null;
    /** Per-tenant health, merged into the list for an at-a-glance column. */
    health: Map<string, AccountHealthRow> = new Map();
    loading = true;
    error: string | null = null;
    /** Tenant ids with an action in flight — drives per-row spinners/disabled state. */
    busy = new Set<string>();

    constructor(
        private readonly service: OperatorTenantsService,
        private readonly plansService: OperatorPlansService,
        private readonly healthService: OperatorHealthService,
    ) {
        super();
    }

    healthFor(tenantId: string): AccountHealthRow | undefined {
        return this.health.get(tenantId);
    }

    listPlans(): Promise<PlanSummary[]> {
        return this.plansService.list();
    }

    getQuotas(tenantId: string): Promise<QuotaBucket[]> {
        return this.plansService.getTenantQuotas(tenantId);
    }

    async assignPlan(tenantId: string, planId: string): Promise<void> {
        await this.run(tenantId, () => this.plansService.assignPlan(tenantId, planId));
    }

    async load(): Promise<void> {
        this.loading = true;
        this.error = null;
        this.notifyChange();
        try {
            const [tenants, health] = await Promise.all([
                this.service.listTenants(),
                this.healthService.listAccountHealth().catch(() => [] as AccountHealthRow[]),
            ]);
            this.tenants = tenants;
            this.health = new Map(health.map((h) => [h.tenantId, h]));
        } catch (e) {
            this.tenants = null;
            this.error = e instanceof Error ? e.message : 'Failed to load tenants';
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    isBusy(tenantId: string): boolean {
        return this.busy.has(tenantId);
    }

    async provision(input: ProvisionInput): Promise<ProvisionHandoff | null> {
        this.busy.add(input.tenantId);
        this.notifyChange();
        try {
            const handoff = await this.service.provision(input);
            await this.load();
            return handoff;
        } finally {
            this.busy.delete(input.tenantId);
            this.notifyChange();
        }
    }

    /**
     * Provision, then (optionally) assign a catalog plan and apply an entitlement
     * override in one busy-tracked flow — so the operator can pick + customize the
     * plan at provision time. Plan assignment seeds features + quota buckets from
     * the plan; the override then fine-tunes features/seat cap (quotas are
     * preserved server-side). The handoff is returned before the list reloads.
     */
    async provisionWithPlan(
        input: ProvisionInput,
        opts: { planId?: string; override?: EntitlementInput },
    ): Promise<ProvisionHandoff | null> {
        this.busy.add(input.tenantId);
        this.notifyChange();
        try {
            const handoff = await this.service.provision(input);
            if (opts.planId) await this.plansService.assignPlan(input.tenantId, opts.planId);
            if (opts.override) await this.service.saveEntitlement(input.tenantId, opts.override);
            await this.load();
            return handoff;
        } finally {
            this.busy.delete(input.tenantId);
            this.notifyChange();
        }
    }

    resendInvite(tenantId: string): Promise<{ adminEmail: string; emailSent: boolean }> {
        return this.service.resendInvite(tenantId);
    }

    generateAdminTempPassword(tenantId: string): Promise<{ adminEmail: string; password: string }> {
        return this.service.generateAdminTempPassword(tenantId);
    }

    async suspend(tenantId: string, reason?: string): Promise<void> {
        await this.run(tenantId, () => this.service.suspend(tenantId, reason));
    }

    async resume(tenantId: string): Promise<void> {
        await this.run(tenantId, () => this.service.resume(tenantId));
    }

    async offboard(tenantId: string, reason?: string): Promise<void> {
        await this.run(tenantId, () => this.service.offboard(tenantId, reason));
    }

    getEntitlement(tenantId: string): Promise<OperatorEntitlement | null> {
        return this.service.getEntitlement(tenantId);
    }

    async saveEntitlement(tenantId: string, input: EntitlementInput): Promise<void> {
        await this.run(tenantId, () => this.service.saveEntitlement(tenantId, input), false);
    }

    /** Runs an action with per-tenant busy tracking; reloads the list on success unless suppressed. */
    private async run(tenantId: string, action: () => Promise<void>, reload = true): Promise<void> {
        this.busy.add(tenantId);
        this.notifyChange();
        try {
            await action();
            if (reload) await this.load();
        } finally {
            this.busy.delete(tenantId);
            this.notifyChange();
        }
    }
}
